/**
 * Sentry Machine ID Setup
 * Sets machineId as a global Sentry tag when it becomes available
 * This enables tracking specific machines across all traces and events
 */

import * as Sentry from '@sentry/react';
import { getElectronStores } from '../stores/electronStores';

let machineIdInitialized = false;

/**
 * Initialize Sentry with machineId as a global tag
 * Call this once when the app starts
 */
export function initializeSentryMachineId(): void {
  if (machineIdInitialized) {
    return;
  }

  const electronStores = getElectronStores();

  // Set immediately if available
  if (electronStores.machineIdState.currentState) {
    const machineId = electronStores.machineIdState.currentState;

    // Set as global tag (appears in all events/spans)
    Sentry.setTag('machine_id', machineId);

    // Also set in user context for easy identification
    Sentry.setUser({
      id: machineId, // This makes it searchable as user ID
    });

    console.log('[SENTRY] Machine ID set:', machineId);
    machineIdInitialized = true;
  }

  // Otherwise, wait for machineId to become available
  console.log('[SENTRY] Waiting for machine ID...');
  const listener = electronStores.machineIdState.addListener((machineId) => {
    if (machineId && !machineIdInitialized) {
      // Set as global tag
      Sentry.setTag('machine_id', machineId);

      // Set in user context
      Sentry.setUser({
        id: machineId,
      });

      console.log('[SENTRY] Machine ID set:', machineId);
      machineIdInitialized = true;

      // Remove listener after setting
      electronStores.machineIdState.removeListener(listener);
    }
  });
}

/**
 * Update Sentry user with additional auth information
 * Call this when user signs in to Clerk
 */
export function updateSentryUser(userData: { username?: string; email?: string; id?: string }): void {
  const electronStores = getElectronStores();
  const machineId = electronStores.machineIdState.currentState;

  Sentry.setUser({
    id: machineId || userData.id, // Prefer machineId as primary ID
    username: userData.username,
    email: userData.email,
    // Store clerk userId as custom field
    clerk_user_id: userData.id,
    ip_address: '{{auto}}',
  });

  console.log('[SENTRY] Updated user with auth info');
}

